import React, { Dispatch, useState, useCallback, useMemo } from "react";
import { connect } from "react-redux";
import { RootState, actions } from "../../store";
import { ThunkDispatch } from "redux-thunk";
import { Action, AnyAction } from "redux";
import "./Authorization.scss";
import ActivationKeyModal from "../ActivationKeyModal/ActivationKeyModal";
import { localStorageSetItem } from "../../utils/storage";
import { useHistory } from "react-router";
import {
  ISendActivationKey,
  UserType,
} from "../../webServer/controllers/commonWeb/types/type";
import TAXI_PARAMS from "../../utils/build";
import { useTranslation } from "react-i18next";
import {
  IonHeader,
  IonToolbar,
  IonTitle,
  IonIcon,
  IonButtons,
  IonContent,
  IonItem,
  IonLabel,
  IonInput,
  IonPage,
  IonList,
  IonButton,
  IonText,
} from "@ionic/react";
import { chevronBackOutline, mailOutline } from "ionicons/icons";

type Props = ReturnType<typeof mapDispatchToProps> &
  ReturnType<typeof mapStateToProps>;

const Authorization: React.FC<Props> = (props) => {
  const { t } = useTranslation();
  const history = useHistory();
  const {
    sendActivationKeyAction,
    error,
    activationKeyOpen,
    activationKeyOpenAction,
  } = props;
  
  const [phone, setPhone] = useState("");
  const [name, setName] = useState("");

  const translations = useMemo(() => ({
    authorization: t("authorization"),
    enterPhoneNumber: t("enterPhoneNumber"),
    phoneNumber: t("phoneNumber"),
    yourName: t("yourName"),
    getSMS: t("getSMS"),
  }), [t]);

  const handlePhoneChange = useCallback((e: CustomEvent) => {
    setPhone(e.detail.value || "");
  }, []);

  const handleNameChange = useCallback((e: CustomEvent) => {
    setName(e.detail.value || "");
  }, []);

  const handleGoBack = useCallback(() => {
    history.goBack();
  }, [history]);

  const keyModalOpen = useCallback(() => {
    if (!phone.trim()) return;

    localStorageSetItem("name", name);
    
    const parameters: ISendActivationKey = {
      phoneOrEmail: `+${phone.replace("+", "")}`,
      userType: UserType.customer,
      licenceKey: TAXI_PARAMS.WEBSERVER_APIKEY || '',
    };

    sendActivationKeyAction(parameters);
  }, [phone, name, sendActivationKeyAction]);

  const keyModalClose = useCallback(() => {
    activationKeyOpenAction(false);
  }, [activationKeyOpenAction]);

  const isFormValid = useMemo(() => 
    phone.trim().length > 0, 
    [phone]
  );

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar>
          <IonButtons slot="start">
            <IonButton onClick={handleGoBack} fill="clear">
              <IonIcon icon={chevronBackOutline} slot="icon-only" />
            </IonButton>
          </IonButtons>
          <IonTitle>{translations.authorization}</IonTitle>
        </IonToolbar>
      </IonHeader>
      
      <IonContent className="authorization">
        <IonList>
          <IonLabel className="authorization__title">
            {translations.enterPhoneNumber}
          </IonLabel>
          
          <IonItem className="authorization__item">
            <IonLabel position="floating">
              {translations.phoneNumber}*
            </IonLabel>
            <IonInput
              value={phone}
              onIonInput={handlePhoneChange}
              clearInput
              required={true}
              type="tel"
              placeholder="+7 XXX XXX XX XX"
              inputmode="numeric"
            />
          </IonItem>
          
          {error && (
            <IonText color="danger" className="errorMessage">
              {error}
            </IonText>
          )}
          
          <IonItem className="authorization__item">
            <IonLabel position="floating">
              {translations.yourName}
            </IonLabel>
            <IonInput
              value={name}
              onIonInput={handleNameChange}
              clearInput
              placeholder={t("enterYourName")}
              autocapitalize="words"
            />
          </IonItem>
          
          <div className="authorization__btnRow">
            <IonButton 
              onClick={keyModalOpen}
              disabled={!isFormValid}
              expand="block"
            >
              <IonIcon slot="start" icon={mailOutline} />
              {translations.getSMS}
            </IonButton>
          </div>
        </IonList>
      </IonContent>
      
      <ActivationKeyModal
        keyModal={activationKeyOpen}
        keyModalClose={keyModalClose}
      />
    </IonPage>
  );
};

const mapStateToProps = (state: RootState) => ({
  error: state.registration.error,
  activationKeyOpen: state.registration.activationKeyOpen,
});

const mapDispatchToProps = (
  dispatch: Dispatch<Action> & ThunkDispatch<any, any, AnyAction>
) => ({
  sendActivationKeyAction: (parameter: ISendActivationKey) =>
    dispatch(actions.registration.sendActivationKeyAction(parameter)),
  activationKeyOpenAction: (activationKeyOpen: boolean) =>
    dispatch(actions.registration.activationKeyOpenAction(activationKeyOpen)),
});

export default React.memo(connect(mapStateToProps, mapDispatchToProps)(Authorization));